import styled, { css } from 'styled-components';

const Button = styled.button.attrs(props => ({
  // Convert the block boolean prop to a string attribute if present
  block: props.block ? 'true' : undefined
}))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  font-size: ${props => props.theme.fontSize.md};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};
  border: none;
  outline: none;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
    transition: all 0.6s ease;
  }
  
  &:hover::before {
    transform: translateX(100%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &::before {
      display: none;
    }
  }
  
  /* Primary Button */
  ${props => props.variant === 'primary' || !props.variant ? css`
    background: linear-gradient(45deg, ${props.theme.colors.primary}, ${props.theme.colors.secondary});
    color: ${props.theme.colors.text.primary};
    box-shadow: 0 4px 15px ${props.theme.colors.primaryAlpha(0.3)};
    
    &:hover:not(:disabled) {
      box-shadow: 0 6px 20px ${props.theme.colors.primaryAlpha(0.5)};
      transform: translateY(-2px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 10px ${props.theme.colors.primaryAlpha(0.3)};
    }
  ` : ''}
  
  /* Secondary Button */
  ${props => props.variant === 'secondary' && `
    background: ${props.theme.colors.primaryAlpha(0.1)};
    color: ${props.theme.colors.text.primary};
    border: 1px solid ${props.theme.colors.primary};
    
    &:hover {
      background: ${props.theme.colors.primaryAlpha(0.2)};
      border-color: ${props.theme.colors.accent};
    }
  `}
  
  /* Success Button */
  ${props => props.variant === 'success' ? css`
    background: #27AE60;
    color: ${props.theme.colors.text.primary};
    box-shadow: 0 4px 15px rgba(39, 174, 96, 0.3);
    
    &:hover:not(:disabled) {
      background: #2ECC71;
      box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4);
      transform: translateY(-2px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 10px rgba(39, 174, 96, 0.3);
    }
  ` : ''}
  
  /* Accent Button */
  ${props => props.variant === 'accent' ? css`
    background: ${props.theme.colors.accent};
    color: ${props.theme.colors.text.primary};
    box-shadow: 0 4px 15px ${props.theme.colors.accentAlpha(0.3)};
    
    &:hover:not(:disabled) {
      background: ${props.theme.colors.secondary};
      box-shadow: 0 6px 20px ${props.theme.colors.accentAlpha(0.4)};
      transform: translateY(-2px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 10px ${props.theme.colors.accentAlpha(0.3)};
    }
  ` : ''}
  
  /* Danger Button */
  ${props => props.variant === 'danger' ? css`
    background: ${props.theme.colors.status.error};
    color: ${props.theme.colors.text.primary};
    box-shadow: 0 4px 15px rgba(255, 94, 91, 0.3);
    
    &:hover:not(:disabled) {
      background: #ff7875;
      box-shadow: 0 6px 20px rgba(255, 94, 91, 0.4);
      transform: translateY(-2px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 10px rgba(255, 94, 91, 0.3);
    }
  ` : ''}
  
  /* Size variants */
  ${props => props.size === 'small' ? css`
    padding: ${props.theme.spacing.xs} ${props.theme.spacing.md};
    font-size: ${props.theme.fontSize.sm};
  ` : props.size === 'large' ? css`
    padding: ${props.theme.spacing.md} ${props.theme.spacing.xl};
    font-size: ${props.theme.fontSize.lg};
  ` : ''}
  
  /* Block (full-width) */
  ${props => props.block ? css`
    width: 100%;
  ` : ''}
`;

export default Button; 